import type TimeStamp           from "@/core/types/TimeStamp";
import serializeSimplified      from '@/core/misc/decorators/SerializeSimplified';
import Title_ru_RU_Class        from "@/core/misc/decorators/Title_ru_RU_Class";
import Title_ru_RU              from '@/core/misc/decorators/Title_ru_RU';
import TYPES                    from '@/core/services/Changelogs/types';
import Core                     from '@/core/Core';
import Utils                    from '@/core/utils/Utils';
import DateRange                from '@/core/types/DateRange';
import DateSimple               from '@/core/types/DateSimple';
import AbstractOrderOption      from '@/domains/Abstract/AbstractOrderOption';
import OfferCharacteristic      from '@/domains/Offers/OfferCharacteristic';
import Characteristic           from '@/domains/Characteristics/Characteristic';
import CharacteristicsService   from '@/domains/Characteristics/CharacteristicsService';
import ProductCategory          from '@/domains/ProductCategories/ProductCategory';
import PaymentTerm              from '@/domains/PaymentTerms/PaymentTerm';
import PaymentTermsService      from '@/domains/PaymentTerms/PaymentTermsService';
import Multiplier               from "@/core/types/Multiplier";
import Tender from "@/domains/Tenders/Tender";

/**
 * Товарные предложения.
 * Симметрично бэку: Offer extends AbstractOrderOption.
 */
@Title_ru_RU_Class('Предложение')
class Offer extends AbstractOrderOption {

  static Events = {
    Created:        'offer:created',
    Updated:        'offer:updated',
    Deleted:        'offer:deleted',
  } as const;

  /** Образцы */
  @Title_ru_RU('образцы', TYPES.CUSTOM_BOOLEAN)
  public isSamples: boolean = false;

  /** Опубликовал ли поставщик предложение */
  @Title_ru_RU('Публикация', TYPES.CUSTOM_BOOLEAN)
  public isPublished: boolean = true;

  /** Период публикации */
  @Title_ru_RU('Период публикации', TYPES.DATE_RANGE)
  public publicationPeriod: DateRange = new DateRange();

  /** Категория товара */
  @Title_ru_RU('Категория товара', TYPES.OBJECT)
  @serializeSimplified
  protected _productCategory: ProductCategory | null = null;

  /** Версия оферты */
  public contractVersion: number | null = null;

  /** Доступность предзаказа */
  @Title_ru_RU('Доступность предзаказа', TYPES.BOOLEAN)
  protected _isAcceptingPreorder: boolean = false;

  /** Значения характеристик оффера — конкретные значения под рамки продукта */
    // @NoDiff на бэке — на фронте дифф пишется отдельно при необходимости
  public characteristics: OfferCharacteristic[] = [];

  static isOffer(value: unknown): value is Offer {
    return value instanceof Offer;
  }

  /**
   * Дата курса валюты — для офера это «сейчас».
   * Симметрично с бэком: Offer::getDateForCurrencyRate() → new Timestamp('now').
   */
  getDateForCurrencyRate(): DateSimple {
    return new DateSimple();
  }

  /** ================ Статические конструкторы ================ */

  static fromServer(data: any, offer: Offer = new Offer()): Offer {
    super.fromServer(data, offer);

    offer._productCategory   = Utils.hydrate(data.productCategory, ProductCategory.fromServer);
    offer.characteristics    = Utils.hydrateArray(data.characteristics, OfferCharacteristic.fromServer);
    offer.publicationPeriod  = Utils.hydrate(data.publicationPeriod, DateRange.fromServer);

    return offer;
  }

  /**
   * Новый офер с проинициализированными дефолтами.
   * По канону — fromNew() (ранее был initDefaultValues).
   */
  static fromNew(): Offer {
    const result = new Offer();

    result._seller = Core.currentPartner!;

    /** выводим по умолчанию: */
    /** Сибирское здоровье */
    result.buyer = Core.sibWellPartner;
    /** Бердск */
    result.buyerStore = Core.sibWellPartner.stores.find((item: any) => item.id === 47);


    /** Если у поставщика тестовый доступ, то он может создать только неактивный офер */
    if (result.seller.isOperationsAllowed === false) {
      result.isPublished = false;
    }

    result._deliveryType = Core.dictionaries.deliveryTypes.get('byETP');
    result._deliveryDays = 12;

    result.calculatePriceMod();

    result._offerCurrencyCode = Core.displayedCurrencyCode;

    result.shipmentPeriod = new DateRange();

    return result;
  }

  /**
   * Создать офер на основе потребности.
   * Используется при инициализации офера из контекста тендера/потребности.
   */
  static fromAbstractDemand(instanceOfAbstractDemand: any): Offer {
    const result = Offer.fromNew();

    result.buyer      = instanceOfAbstractDemand.buyer;
    result.buyerStore = instanceOfAbstractDemand.buyerStore;

    return result;
  }

  setTender(tender: Tender): void {
    this.buyer = tender.buyer;
    this.buyerStore = tender.buyerStore
  }

  set sellerCompany(value: any) {
    super.sellerCompany = this._seller.companies.find((_item: any) => _item.id === value.id);
  }

  get sellerCompany() {
    return super.sellerCompany;
  }

  /**
   * Категория приходит полной — с единицами, НДС, отсрочкой и наценкой:
   * её грузит источник селекта. Поэтому здесь сразу раскладываем дефолты
   * по оферу, догружать нечего.
   *
   * Единицу измерения не трогаем: её подставляет свой источник, он же
   * зависит от категории.
   */
  set productCategory(value: ProductCategory | null) {
    this._productCategory = value;

    if (!value) return;

    this.vatRateBuyer = value.vatRate;

    /** Меняем дни у самого условия: присваивание в this.paymentTerm вызвало
     *  бы сеттер, а тот тянет за собой пересчёт отсрочки. */
    this.paymentTerm.days = value.paymentDelay;

    this.calculatePriceMod();

    /** Рамки характеристик — единственное, чего в категории нет.
     *  Когда появятся рамки продукта, запрос дополнится productId. */
    CharacteristicsService.getAttachedList({ categoryId: value.id })
      .then((frames: Characteristic[]) => {
        this.initCharacteristicsFromFrames(frames);
      });
  }

  get productCategory(): ProductCategory | null {
    return this._productCategory;
  }

  /**
   * Создаёт пустые значения OfferCharacteristic по набору рамок.
   * Значения (stringValue/numericValue/listValue/entityPointerValues) остаются
   * пустыми — поставщик заполнит их в форме оффера.
   */
  private initCharacteristicsFromFrames(frames: Characteristic[]): void {
    this.characteristics = frames.map((characteristic: Characteristic) => {
      const value = new OfferCharacteristic();
      value.characteristic = characteristic;
      value.valueType      = characteristic.valueType;
      return value;
    });
  }

  set isAcceptingPreorder(value: boolean) {
    this._isAcceptingPreorder = value;

    if (this._isAcceptingPreorder === false) {
      this.preorderMinQty         = null;
      this.preorderMonthlyReserve = null;
    }
  }

  get isAcceptingPreorder(): boolean {
    return this._isAcceptingPreorder;
  }

  /** Значения характеристик у предложения свои. */
  getCharacteristics(): OfferCharacteristic[] {
    return this.characteristics;
  }

  /** ================ Расчёты ================ */

  calculatePriceMod(): void {
    if (this.productCategory && this.deliveryType) {

      let additionalPayment = this.productCategory.priceModProfit;

      if (this.deliveryType.id === 'byETP' && this.deliveryPricePerUnit!.isEmpty()) {
        additionalPayment = additionalPayment.plus(this.productCategory.priceModTransport);
      }
      else {
        /** Доставка не платформой либо стоимость доставки уже задана явно —
         *  транспортную составляющую в наценку не закладываем. */
      }

      this.priceMod = additionalPayment;
    }
    else {
      /** Категория или тип доставки ещё не выбраны — считать наценку не из чего.
       *  priceMod остаётся тем, чем его проинициализировали при объявлении. */
    }
  }

  calculatePlannedPaymentDateToSeller(): void {
    this.plannedPaymentDateToSeller = this.paymentTerm.getRelativePaymentDate(new DateSimple(), this.deliveryDays);
  }

  calculatePlannedPaymentDateByBuyer(): void {
    this.plannedPaymentDateByBuyer = new PaymentTerm(
      PaymentTermsService.getEventById('after_receiving'),
      this.productCategory!.paymentDelay,
    ).getRelativePaymentDate(new DateSimple(), this.deliveryDays);
  }

  async calculatePriceFullBuyerWithDeferredPayment(): Promise<void> {
    if (!this.productCategory?.paymentDelay) return;

    await super.calculatePriceFullBuyerWithDeferredPayment();
  }

  setValuesBasedOnSellerCompany(): void {
    if (!this.sellerCompany) return;

    if (this.sellerCompany.isVatPayer) {
      this.vatRateSeller       = this.sellerCompany.getVatRate();
      this.isVatIncludedSeller = true;
    } else {
      this.vatRateSeller       = null;
      this.isVatIncludedSeller = null;
    }
  }

  resetDeferredPayment(): void {
    this.plannedPaymentDateByBuyer         = null as any;
    this.plannedPaymentDateToSeller        = null as any;
    this.priceFullBuyerWithDeferredPayment = null as any;
    this.priceModDeferredPayment           = Multiplier.fromPercent(0);
  }

  /** ================ Актуальность ================ */

  get relevantTo(): TimeStamp | undefined {
    return this.updatedAt?.addDays(19);
  }

  get isActual(): boolean | undefined {
    return this.relevantTo?.isFuture();
  }
}

export default Offer;
